import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { getPublicSharedRecordPage, type PublicSharedRecordPage as SharedRecordPageData } from '../lib/api';
import './SharedRecords.css';

function formatRecordType(value: string) {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function SharedRecordsPage({ token }: { token: string }) {
  const [page, setPage] = useState<SharedRecordPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const previousTitle = document.title;
    document.title = 'Shared pet records | Actapet';
    const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]') || document.createElement('meta');
    const createdRobots = !robots.isConnected;
    robots.name = 'robots';
    robots.content = 'noindex, nofollow, noarchive';
    if (createdRobots) document.head.append(robots);

    const loadPage = async () => {
      try {
        const result = await getPublicSharedRecordPage(token);
        if (active) setPage(result);
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : 'This temporary records link is no longer available');
      } finally {
        if (active) setLoading(false);
      }
    };
    void loadPage();
    return () => {
      active = false;
      document.title = previousTitle;
      if (createdRobots) robots.remove();
    };
  }, [token]);

  return <div className="shared-record-page">
    <main className="shared-record-main">
      {page ? <>
        <div className="shared-record-heading">
          <div className="shared-record-heading-copy">
            <p className="eyebrow">Shared pet records</p>
            <h1>Records for {page.owner.name}'s pets</h1>
          </div>
          <aside className="shared-record-facility" aria-label="Facility contact information">
            <strong>{page.facility.name}</strong>
            {page.facility.address && <span>{page.facility.address}</span>}
            {page.facility.phone && <a href={`tel:${page.facility.phone}`}>{page.facility.phone}</a>}
            {page.facility.email && <a href={`mailto:${page.facility.email}`}>{page.facility.email}</a>}
          </aside>
        </div>
        <dl className="shared-record-contact">
          <div><dt>Email</dt><dd><a href={`mailto:${page.owner.email}`}>{page.owner.email}</a></dd></div>
          <div><dt>Phone</dt><dd>{page.owner.phone ? <a href={`tel:${page.owner.phone}`}>{page.owner.phone}</a> : 'Not provided'}</dd></div>
        </dl>
        <p className="shared-record-expiry">Temporary access expires {new Date(page.expires_at).toLocaleString()}.</p>
        {page.pets.length === 0 ? <p className="shared-record-empty">There are no active pets on this records page.</p> : <div className="shared-record-table-scroll">
          <table className="shared-record-table">
            <thead><tr><th scope="col">Pet Name</th><th scope="col">Document Type</th><th scope="col">Link</th></tr></thead>
            {page.pets.map((pet) => <tbody key={pet.id}>
              {pet.records.length ? pet.records.map((record) => <tr key={record.id}>
                <td>{pet.name}</td>
                <td>{formatRecordType(record.record_type)}</td>
                <td>{record.view_url ? <a href={record.view_url} target="_blank" rel="noreferrer">{record.title}<ExternalLink size={14} aria-hidden="true" /></a> : <span className="shared-record-no-file">{record.title} · No file available</span>}</td>
              </tr>) : <tr key={`${pet.id}-empty`}><td>{pet.name}</td><td>No records</td><td>—</td></tr>}
            </tbody>)}
          </table>
        </div>}
      </> : <section className="shared-record-status" role={error ? 'alert' : 'status'}>
        <p className="eyebrow">Shared pet records</p>
        <h1>{loading ? 'Loading records...' : 'Records link unavailable'}</h1>
        {error && <p>{error}</p>}
      </section>}
    </main>
    <footer className="shared-record-footer"><a className="shared-record-home-link" href="https://actapet.com" target="_blank" rel="noreferrer" aria-label="Visit Actapet"><img src="/actapet-dark%20logo%20transparent.png" alt="Actapet" /></a></footer>
  </div>;
}